import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FilePlus, Trash2 } from "lucide-react";
import { endpoints } from "@/lib/api";
import { toast } from "sonner";

const empty = {
  client: "",
  client_email: "",
  invoice_date: new Date().toISOString().slice(0, 10),
  due_date: (() => { const d = new Date(); d.setDate(d.getDate() + 30); return d.toISOString().slice(0, 10); })(),
  description: "",
  amount: 0,
  amount_paid: 0,
  status: "Draft",
  notes: "",
};

const STATUSES = ["Draft", "Sent", "Paid", "Partially Paid"];

export default function InvoiceModal({ open, onOpenChange, initial, onChanged }) {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const isEdit = !!initial?.id;

  useEffect(() => {
    if (open) setForm({ ...empty, ...(initial || {}) });
  }, [open, initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client.trim()) return toast.error("Client is required");
    if (!form.amount || form.amount <= 0) return toast.error("Amount must be greater than 0");
    setSaving(true);
    try {
      const payload = { ...form, amount: Number(form.amount), amount_paid: Number(form.amount_paid || 0) };
      if (isEdit) {
        await endpoints.updateInvoice(initial.id, payload);
        toast.success("Invoice updated");
      } else {
        await endpoints.createInvoice(payload);
        toast.success("Invoice created");
      }
      onChanged?.();
    } catch { toast.error("Could not save invoice"); } finally { setSaving(false); }
  };

  const remove = async () => {
    setSaving(true);
    try {
      await endpoints.deleteInvoice(initial.id);
      toast.success("Invoice deleted");
      onChanged?.();
    } catch { toast.error("Could not delete"); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="modal-invoice" className="sm:max-w-[560px] max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl brand-gradient text-white flex items-center justify-center shadow-md shadow-indigo-500/25">
              <FilePlus className="h-4.5 w-4.5" />
            </div>
            <div>
              <DialogTitle>{isEdit ? `Invoice ${initial.invoice_number}` : "New invoice"}</DialogTitle>
              <DialogDescription>{isEdit ? "Edit invoice or record a payment." : "Add a new billing record for a client."}</DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3.5 pt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="i-client">Client</Label>
              <Input data-testid="invoice-client-input" id="i-client" value={form.client} onChange={(e) => set("client", e.target.value)} placeholder="Client name" />
            </div>
            <div>
              <Label htmlFor="i-email">Client email</Label>
              <Input data-testid="invoice-email-input" id="i-email" type="email" value={form.client_email} onChange={(e) => set("client_email", e.target.value)} placeholder="ap@client.com" />
            </div>
          </div>
          <div>
            <Label htmlFor="i-desc">Description</Label>
            <Input data-testid="invoice-description-input" id="i-desc" value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="ISOQuest services provided" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="i-date">Invoice date</Label>
              <Input data-testid="invoice-date-input" id="i-date" type="date" value={form.invoice_date} onChange={(e) => set("invoice_date", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="i-due">Due date</Label>
              <Input data-testid="invoice-due-input" id="i-due" type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label htmlFor="i-amt">Amount ($)</Label>
              <Input data-testid="invoice-amount-input" id="i-amt" type="number" step="0.01" min="0" value={form.amount} onChange={(e) => set("amount", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="i-paid">Amount paid ($)</Label>
              <Input data-testid="invoice-paid-input" id="i-paid" type="number" step="0.01" min="0" value={form.amount_paid} onChange={(e) => set("amount_paid", e.target.value)} />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="invoice-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label htmlFor="i-notes">Notes</Label>
            <Textarea data-testid="invoice-notes-input" id="i-notes" rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} />
          </div>
          <DialogFooter className="flex-wrap gap-2">
            {isEdit && (
              <Button data-testid="invoice-delete-button" type="button" variant="ghost" className="text-rose-600 mr-auto" onClick={remove} disabled={saving}>
                <Trash2 className="h-4 w-4 mr-1" /> Delete
              </Button>
            )}
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
            <Button data-testid="invoice-submit-button" type="submit" disabled={saving} className="brand-gradient text-white">
              {saving ? "Saving..." : isEdit ? "Save changes" : "Create invoice"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
